import { type AxiosInstance, HttpStatusCode, isAxiosError } from "axios";
import { type FormData as MapSearchFormDataType } from "../components/MapSearchDrawer";
import { SendToTrackingService } from "@/api/handlers/errors";

const TIMEOUT_MILLISECOND = 60_000;

interface GetMapRealEstateSearchAPIResponse {
  data?: GetMapRealEstateSearchAPIResponseBody;
  status: number;
  error?: string;
  timeout?: boolean;
}

interface GetMapRealEstateSearchAPIResponseBody {
  count?: number;
  list?: {
    id: number;
    landId: number;
    buildingId: number;
    buildingName: string;
    realEstateType: string;
    prefName: string;
    cityName: string;
    location: string;
    chibanKaokuNumber: string;
    address: string;
    area: number | null;
    latitude: number;
    longitude: number;
  }[];
}

class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TimeoutError";
  }
}

export const getMapRealEstateSearchAPI = async (
  apiClient: AxiosInstance,
  data: MapSearchFormDataType
): Promise<GetMapRealEstateSearchAPIResponse | undefined> => {
  // タイムアウトの実装
  const timeoutPromise = new Promise((_resolve, reject) =>
    setTimeout(() => {
      reject(new TimeoutError("不動産検索がタイムアウトしました"));
    }, TIMEOUT_MILLISECOND)
  );

  try {
    const post = apiClient.post<GetMapRealEstateSearchAPIResponseBody>(
      "map/real-estate-search",
      data
    );
    const response = (await Promise.race([
      post,
      timeoutPromise,
    ])) as GetMapRealEstateSearchAPIResponse;

    return {
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    if (error instanceof TimeoutError) {
      return {
        status: HttpStatusCode.Ok,
        timeout: true,
      };
    } else if (isAxiosError<GetMapRealEstateSearchAPIResponse>(error)) {
      if (
        (error.response?.status ?? HttpStatusCode.InternalServerError) >=
        HttpStatusCode.InternalServerError
      ) {
        SendToTrackingService(error);
      }

      // Unauthorized以外のエラーが発生した場合、ステータスコードを返す
      if (error.response?.status !== HttpStatusCode.Unauthorized) {
        return {
          status: error.response?.status ?? HttpStatusCode.InternalServerError, // ステータスコードが取得できない場合は500を返す
          error: error.response?.data?.error,
        };
      }
    }

    // その他のエラーの場合はundefinedを返す
    return undefined;
  }
};
